import {useMemo} from 'react'
import logger from '../../utils/logger-instance'

/**
 * Builds a map of stacked image base ids to selected colors from products and nested set products.
 *
 * @param {Object[]} products - Products that contribute stacked image colors.
 * @returns {Object.<string, string>} Selected colors by stacked image base.
 */
export const getColorsByBase = (products) =>
    Object.fromEntries(
        [
            ...(products?.map((product) => [product.c_stackedImageBase, product]) ?? []),
            ...(products?.flatMap((product) =>
                (product.setProducts || []).map((setProduct) => [
                    setProduct.c_stackedImageBase,
                    setProduct
                ])
            ) ?? [])
        ]
            .map(([base, product]) => [base, product?.variationValues?.color])
            .filter(([base, color]) => base && color)
    )

/**
 * Extracts stacked image base id from a brandmachine image link.
 *
 * @param {string} link - Stacked image source link.
 * @returns {string|null} Stacked image base id when the link matches the expected format.
 */
export const getStackedImageBaseFromLink = (link) => {
    const brandmachineSplit = link.split('/images/brandmachine/')
    if (brandmachineSplit.length !== 2) {
        return null
    }

    const imageBaseSplit = brandmachineSplit[1].split('/')
    if (imageBaseSplit.length < 2) {
        return null
    }

    return imageBaseSplit[0] || null
}

/**
 * Resolves stacked image urls by replacing `$colorCode` placeholders with selected colors.
 *
 * @param {Object} params - Resolver parameters.
 * @param {Object} [params.imageStacked] - Stacked image configuration object.
 * @param {Object.<string, string>} params.colorsByBase - Selected colors by stacked image base.
 * @param {boolean} [params.enabled=true] - Skip resolution when false.
 * @returns {{alt?: string, images?: string[]}|{}} Resolved stacked image payload.
 */
export const resolveStackedImages = ({imageStacked, colorsByBase, enabled = true}) => {
    if (!enabled || !imageStacked) {
        return {}
    }

    const images = imageStacked.disBaseLinks.map((link) => {
        const base = getStackedImageBaseFromLink(link)
        if (!base) {
            logger.warn(`Invalid link format for stacked image: ${link}`)
            return null
        }

        const color = colorsByBase[base]
        if (!color) {
            logger.error(
                `No color found for stackedImageBase ${base}. Available ${
                    Object.keys(colorsByBase).join(', ') ||
                    `none! Neither product has c_stackedImageBase.`
                }`
            )
            return null
        }
        return link.replace('$colorCode', color)
    })

    return {
        ...(imageStacked.alt != null && {alt: imageStacked.alt}),
        ...(imageStacked.disBaseLinks != null && {
            images: images.some((link) => !link) ? [] : images
        })
    }
}

/**
 * Processes stacked product images by replacing color placeholders with actual color codes
 * from product variation values.
 *
 * Maps products and their set products to their stacked image base identifiers and colors,
 * then replaces the `$colorCode` placeholder in image URLs with the corresponding color value.
 *
 * @param {Object} params - Hook parameters
 * @param {Object} [params.imageStacked] - Stacked image configuration object
 * @param {string} params.imageStacked.alt - Alternative text for the images
 * @param {string[]} params.imageStacked.disBaseLinks - Array of image URLs with `$colorCode` placeholder
 * @param {Array} params.products - Array of product objects with color variation data. MUST be memoized by caller to avoid unnecessary recomputations (e.g., use useMemo or ensure stable reference).
 * @param {string} params.products[].c_stackedImageBase - Base identifier for matching image URLs
 * @param {Object} params.products[].variationValues - Product variation values
 * @param {string} params.products[].variationValues.color - Color code to replace placeholder
 * @param {Array} [params.products[].setProducts] - Array of set products with same structure
 * @param {boolean} [params.enabled=true] - Skip all processing and return an empty object when false
 * @returns {{alt: string, images: string[]}|{}} Object with alt text and processed image URLs, or empty object if imageStacked is not provided
 *
 * @example
 * // Caller should memoize products array to prevent unnecessary recomputations
 * const products = useMemo(() => [product], [product])
 * const stackedImages = useStackedImages({imageStacked, products})
 */
const useStackedImages = ({imageStacked, products, enabled = true}) => {
    const colorsByBase = useMemo(() => {
        if (!enabled || imageStacked?.disBaseLinks?.length <= 0) {
            return {}
        }

        return getColorsByBase(products)
    }, [enabled, imageStacked?.disBaseLinks, products])

    return useMemo(
        () =>
            resolveStackedImages({
                imageStacked,
                colorsByBase,
                enabled
            }),
        [enabled, imageStacked, colorsByBase]
    )
}

export default useStackedImages
